import { offset } from "@floating-ui/dom";
import { createContext } from "react";
import { isMobile } from "react-device-detect";
import Shepherd from "shepherd.js";

export const shepherdTourInstance = new Shepherd.Tour({
  useModalOverlay: true,
  defaultStepOptions: {
    classes: "shadow-md bg-purple-dark minWidthForMobil crazyZ",
    scrollTo: false,
    floatingUIOptions: {
      middleware: [offset({ mainAxis: 12, crossAxis: 0 })],
    },
  },
});
export const ShepherdTourContext = createContext(shepherdTourInstance);

const onShow = (className: string) => () => {
  setTimeout(
    () => {
      // Ensures Shepherd has fully rendered
      const target = document.querySelector(`.${className}`);
      if (target) {
        // Get the exact position of the highlighted element
        const rect = target.getBoundingClientRect();

        // Create the overlay div
        let overlayDiv = document.querySelector(
          "#custom-overlay"
        ) as HTMLElement;
        if (!overlayDiv) {
          overlayDiv = document.createElement("div");
          overlayDiv.id = "custom-overlay";
          document.body.appendChild(overlayDiv);
        }

        // Apply precise positioning
        overlayDiv.style.position = "absolute";
        overlayDiv.style.top = `${window.scrollY + rect.top}px`; // Adjust for scrolling
        overlayDiv.style.left = `${window.scrollX + rect.left}px`;
        overlayDiv.style.width = `${rect.width}px`;
        overlayDiv.style.height = `${rect.height}px`;
        overlayDiv.style.background = "rgba(0, 0, 0, 0)"; // Semi-transparent red
        overlayDiv.style.zIndex = "10000"; // Above Shepherd overlay
        overlayDiv.style.display = "flex";
        overlayDiv.style.justifyContent = "center";
        overlayDiv.style.alignItems = "center";
        overlayDiv.style.color = "white";
        overlayDiv.style.fontWeight = "bold";
        overlayDiv.style.pointerEvents = "auto"; // Prevents interaction interference

        overlayDiv.addEventListener("click", (event) => {
          event.stopPropagation(); // Stops event from reaching elements below
          event.preventDefault(); // Prevents any default action
        });
      }
    },
    isMobile ? 500 : 50
  ); // Small delay to allow Shepherd to render
};

const onHide = () => {
  // Remove overlay when the step is hidden
  document.querySelector("#custom-overlay")?.remove();
};

const mobilStep1: Shepherd.StepOptions = {
  id: "Open Sidebar 1",
  text: "Press here to open the sidebar and see all your campaigns",
  classes: "example-step-extra-class",
  scrollTo: true,
  attachTo: {
    element: ".hamburger",
    on: "right",
  },
};

const mobilStep2: Shepherd.StepOptions = {
  id: "Open Sidebar 2",
  text: "Open the sidebar again",
  classes: "example-step-extra-class",
  scrollTo: true,
  attachTo: {
    element: ".hamburger",
    on: "right",
  },
};

shepherdTourInstance.addSteps([
  {
    id: "Welcome",
    title: "Welcome to Speaking of Me 👋",
    text: "This quick tutorial to get up and running with collecting reviews",
    classes: "example-step-extra-class",
    buttons: [
      {
        text: "Next",
        action: shepherdTourInstance.next,
      },
    ],
  },
  {
    id: "Sidebar",
    text: "This is your first campaign. It was automatically generated and is ready for you to customize",
    attachTo: {
      element: ".firstMenuItem",
      on: isMobile ? "bottom" : "right",
    },
    classes: "example-step-extra-class",
    buttons: [
      {
        text: "Next",
        action: shepherdTourInstance.next,
      },
    ],
    when: {
      show: onShow("firstMenuItem"),
      hide: onHide,
    },
  },
  {
    id: "Three Dots",
    text: "Click to open the Campaign Menu",
    attachTo: {
      element: ".firstContextMenu",
      on: isMobile ? "bottom" : "right",
    },
    classes: "example-step-extra-class",
  },
  {
    id: "Context Menu",
    text: "You can edit your campaign at any time",
    attachTo: {
      element: ".contextMenu",
      on: isMobile ? "bottom" : "right",
    },
    classes: "example-step-extra-class",
    buttons: [
      {
        text: "Next",
        action: shepherdTourInstance.next,
      },
    ],
    when: {
      show: onShow("contextMenu"),
      hide: onHide,
    },
  },
  {
    id: "Click to edit questions",
    text: "Click here to set up your review questions",
    attachTo: {
      element: ".editQuestions",
      on: isMobile ? "bottom" : "right",
    },
    classes: "example-step-extra-class",
  },
  {
    id: "General Campaign Modal",
    text: "These are your campaign details. Reviewers will answer each question in order. Each question will have a separate video response",
    attachTo: isMobile
      ? undefined
      : {
          element: ".shepherdCampaignModal",
          on: "top",
        }, 
    classes: "example-step-extra-class",
    buttons: [
      {
        text: "Next",
        action: shepherdTourInstance.next,
      },
    ],
    when: {
      show: onShow("shepherdCampaignModal"),
      hide: onHide,
    },
  },
  {
    id: "Edit title",
    text: `Click the pencil icon to edit the name of your campaign. Don't stress about it, this name can always be changed and will not be shown to your reviewers`,
    attachTo: {
      element: ".shepardEditableThing",
      on: "top",
    },
    classes: "example-step-extra-class",
    buttons: [
      {
        text: "Next",
        action: shepherdTourInstance.next,
      },
    ],
    canClickTarget: true,
  },
  {
    id: "Delete Questions",
    text: `You can delete a question by pressing the trash can`,
    attachTo: {
      element: ".trashCan",
      on: isMobile ? "bottom" : "right",
    },
    classes: "example-step-extra-class",
    buttons: [
      {
        text: "Next",
        action: shepherdTourInstance.next,
      },
    ],
  },
  {
    id: "Add Question",
    text: `Add new questions by typing them here`,
    attachTo: {
      element: ".campaignModalInput",
      on: "top",
    },
    classes: "example-step-extra-class",
    buttons: [
      {
        text: "Next",
        action: shepherdTourInstance.next,
      },
    ],
  },
  {
    id: "Drag and Drop",
    text: `Drag and drop your questions to rearrange them`,
    attachTo: {
      element: ".campaignModalDragAndDrop",
      on: "top",
    },
    classes: "example-step-extra-class",
    buttons: [
      {
        text: "Next",
        action: shepherdTourInstance.next,
      },
    ],
  },
  {
    id: "Save Campaign",
    text: `When you are satisfied with your campaign, press the save button`,
    attachTo: isMobile ? {} : {
      element: ".shepherdCampaignModal",
      on: "top",
    },
    classes: "example-step-extra-class",
    buttons: isMobile
      ? [{ text: "Next", action: shepherdTourInstance.hide }]
      : [],
  },
  {
    id: "Activate General",
    text: "Now it is time to activate your campaign so that you can start collecting reviews",
    attachTo: isMobile
      ? {}
      : {
          element: ".firstMenuItem",
          on: "right",
        },
    classes: "example-step-extra-class",
    buttons: [
      {
        text: "Next",
        action: shepherdTourInstance.next,
      },
    ],
    when: {
      show: onShow("firstMenuItem"),
      hide: onHide,
    },
  },
  {
    id: "Activate General",
    text: "This indicator shows the campaign's status. Orange is deactivated, green is activated",
    attachTo: {
      element: ".campaignIndicator",
      on: isMobile ? "bottom" : "right",
    },
    classes: "example-step-extra-class",
    buttons: [
      {
        text: "Next",
        action: shepherdTourInstance.next,
      },
    ],
    when: {
      show: onShow("firstMenuItem"),
      hide: onHide,
    },
  },
  {
    id: "Three Dots to activate",
    text: "Open the campaign menu again",
    attachTo: {
      element: ".firstContextMenu",
      on: isMobile ? "bottom" : "right",
    },
    classes: "example-step-extra-class",
  },
  {
    id: "Click to Activate",
    text: "Click here to activate your campaign",
    attachTo: {
      element: ".activateCampaign",
      on: isMobile ? "bottom" : "right",
    },
    classes: "example-step-extra-class",
  },
  {
    id: "Live",
    title: "Congratulations 🎉",
    text: "Your campaign is live!",
    classes: "example-step-extra-class",
    buttons: [
      {
        text: "Next",
        action: shepherdTourInstance.next,
      },
    ],
  },
  {
    id: "Copy Link",
    text: "Click here to copy your link. Using this link, anyone can leave a review and it will show up in your dashboard",
    classes: "example-step-extra-class",
    attachTo: {
      element: ".copyLinkButton",
      on: isMobile ? "top" : "left",
    },
    buttons: [
      {
        text: "Finish",
        action: shepherdTourInstance.next,
      },
    ],
  },
]);

if (isMobile) {
  shepherdTourInstance.addStep(mobilStep1, 1);
  shepherdTourInstance.addStep(mobilStep2, 13);
}
